// React Imports
import { lazy, memo, Suspense, useEffect, useMemo, useState } from "react";

// Third-Party Libraries
import cx from "classnames";
import isEmpty from "lodash/isEmpty";
import isNull from "lodash/isNull";

// Common Utils

// Ionic and Icon Imports
import {
  IonLabel,
  IonToast,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";

// Constants and Configs
import { WLCOLORS } from "../../../../Common/config/global";

// Stores
import useWLPromoStore from "../../../components/WLPromoField/store";
import { usePurchaseV2Store } from "./store";

// Components
import s from "./PaymentV2.module.scss";
import { WLSubscriptionResponseDialog } from "../../../components/WLSubscriptionResponseDialog/WLSubscriptionResponseDialog";
import PaymentHeader from "./PaymentHeader/PaymentHeader";
import AttemptCounts from "./AttemptCounts/AttemptCounts";
import PastWinners from "./PastWinners/PastWinners";
import CurrentEntries from "./CurrentEntries/CurrentEntries";

import { includes } from "lodash";
import {
  useSuperGiveAwayRunningGiveawayProvider,
  useUpcomingGiveAwaysListProvider,
} from "../../../CommonStores/giveaways/useGiveAwaysProvider";
import { useBreakpoint } from "../../../../Common/utils/common";
import Giveaways from "./Giveaways/Giveaways";
import WLStripePaymentMethods from "../../../components/WLStripePaymentMethods/WLStripePaymentMethods";
import useWebPaymentV2Provider, { PurchaseType } from "./useWebPaymentProvider";
import { useUserSubscriptionsProvider } from "../../../CommonStores/subscriptions/useSubscriptionsProvider";
import useExistingStripeSubscriptionProvider from "../../../components/WLStripePaymentMethods/setupProviders/useExistingStripeSubscriptionProvider";
import SuccessRetryPaymentListener from "./SuccessPaymentListeners/SuccessRetryPaymentListener/SuccessRetryPaymentListener";
import RetryPaymentWStripeFormWrapper from "./RetryPaymentProvider/RetryPaymentProviderWStripeForm/RetryPaymentWStripeFormWrapper";
import RetryPaymentWCardWrapper from "./RetryPaymentProvider/RetryPaymentWCard/RetryPaymentWCardWrapper";
import RetryPaymentsOrderSummary from "./RetryPaymentsOrderSummary/RetryPaymentsOrderSummary";
import { WLButton } from "../../../../Common/components/WLButton/WLButton";
import { useWebUserStore } from "../../../CommonStores";
import { shallow } from "zustand/shallow";
import { signOut } from "../../../services/user";
import { iWLPromoField } from "../../../../Common/models/promos";
import WLPromoField from "../../../components/WLPromoField/WLPromoField";
import useWLConfigStore from "../../../CommonStores/wlConfig/store";
import useFailedPaymentOfferStore from "../../../CommonStores/failedPaymentOffer/failedPaymentOfferStore";
import Stripe from "stripe";
import PaymentDrawTonight from "./PaymentDrawTonight";

const PaymentV2Content = () => {
  const { mode, isDrawTonightMode } = useWebPaymentV2Provider();

  const breakpoint = useBreakpoint();
  const [user, latestFailedStripePaymentAttempt] = useWebUserStore(
    (store) => [store.user, store.latestFailedStripePaymentAttempt],
    shallow,
  );
  const [wlConfig] = useWLConfigStore((state) => [state.wlConfig], shallow);

  const [failedPaymentSubscription] = useFailedPaymentOfferStore(
    (state) => [state.failedPaymentSubscription],
    shallow,
  );

  const isMobile = breakpoint === "mobile";
  useExistingStripeSubscriptionProvider();
  useUserSubscriptionsProvider();
  useSuperGiveAwayRunningGiveawayProvider();
  useUpcomingGiveAwaysListProvider();

  const {
    createAndRetryPaymentInvoice,
    selectedProduct,
    error,
    showStripePaymentMethod,
    setError,
  } = usePurchaseV2Store();

  const validModes = ["retryPayment"];

  const showInvalidLink = !includes(validModes, mode);

  const renderWLStripPayment = useMemo(() => {
    return (
      showStripePaymentMethod && (
        <div className={s.stripeContainer}>
          <WLStripePaymentMethods
            darkMode
            hasOtherPaymentMethod={
              mode === undefined || mode === "retryPayment"
            }
          />
        </div>
      )
    );
  }, [showStripePaymentMethod]);

  const {
    validWLPromo,
    promoField,
    validStripeCoupon,
    defaultPromoCode,
    setPromoFieldValue,
    triggerFieldValidation,
  } = useWLPromoStore();

  const [inView, setInView] = useState(true);
  useEffect(() => {
    if (
      wlConfig &&
      defaultPromoCode !== undefined &&
      !isNull(defaultPromoCode) &&
      promoField !== undefined &&
      setPromoFieldValue !== undefined &&
      triggerFieldValidation !== undefined &&
      !validWLPromo &&
      !validStripeCoupon &&
      inView &&
      failedPaymentSubscription &&
      user &&
      latestFailedStripePaymentAttempt
    ) {
      if (wlConfig) {
        const { attempt_count } = latestFailedStripePaymentAttempt.data
          .object as Stripe.Invoice;
        const currentAttemptPromos =
          wlConfig.billingIssuePromoConfig[attempt_count];
        const configPromoForSubscription =
          currentAttemptPromos?.[failedPaymentSubscription.docId];
        if (
          configPromoForSubscription &&
          defaultPromoCode &&
          configPromoForSubscription === defaultPromoCode
        ) {
          setPromoFieldValue(promoField.name, defaultPromoCode);
          triggerFieldValidation();
        }
      }
    }
  }, [
    defaultPromoCode,
    wlConfig,
    promoField,
    setPromoFieldValue,
    triggerFieldValidation,
    inView,
    failedPaymentSubscription,
    user,
    latestFailedStripePaymentAttempt,
  ]);

  const getPromoProps = (mode: PurchaseType): iWLPromoField => {
    switch (mode) {
      case "retryPayment":
        return {
          forBillingIssue: true,
        };
      default:
        return {};
    }
  };

  useIonViewWillEnter(() => {
    setInView(true);
  });

  useIonViewWillLeave(() => {
    setInView(false);
  });

  return !showInvalidLink ? (
    <div className={cx(s.content, { [s.mobile]: isMobile })}>
      <div className={s.promoFieldHidden}>
        {mode !== undefined &&
          mode !== "downgrade" &&
          selectedProduct !== undefined && (
            <WLPromoField
              selectedSubscription={selectedProduct}
              {...getPromoProps(mode)}
              hidden={true}
            />
          )}
      </div>

      {isDrawTonightMode ? (
        <PaymentDrawTonight />
      ) : (
        <>
          <PaymentHeader />
          <AttemptCounts />
          <Giveaways />
          <PastWinners />
          <CurrentEntries />
        </>
      )}

      {renderWLStripPayment}

      {mode === "retryPayment" && (
        <>
          <SuccessRetryPaymentListener />
          {selectedProduct !== undefined && (
            <>
              <RetryPaymentWStripeFormWrapper />
              <RetryPaymentWCardWrapper />
            </>
          )}
        </>
      )}
      <div className={cx(s.stripeContainer, s.footer)}>
        {mode === "retryPayment" && (
          <>
            <RetryPaymentsOrderSummary />

            <div className={s.buttons}>
              <WLButton
                onClick={createAndRetryPaymentInvoice}
                disabled={!user || selectedProduct === undefined}
                className={cx(
                  s.submitButton,
                  "red-submit-button win-body ion-no-margin",
                )}
                isFormComponent
              >
                Make Payment
              </WLButton>
              {!isDrawTonightMode && (
                <WLButton
                  className={cx(s.cancelBtn, "win-body")}
                  onClick={() => {
                    signOut();
                    window.location.href = "https://winnerslocker.com.au/";
                  }}
                  fill="clear"
                >
                  No Thanks
                </WLButton>
              )}
            </div>
          </>
        )}
      </div>

      <WLSubscriptionResponseDialog />

      <IonToast
        isOpen={!isEmpty(error)}
        message={error}
        duration={3000}
        color={WLCOLORS.danger}
        onDidDismiss={() => setError("")}
      />
    </div>
  ) : (
    <div className={cx(s.invalid)}>
      <IonLabel>
        Invalid Membership link, please{" "}
        <a
          className={s.contactLink}
          href="https://winnerslocker.com.au/contact/"
          target="_newTab"
        >
          contact support
        </a>
      </IonLabel>
    </div>
  );
};

const PaymentV2 = () => {
  return <PaymentV2Content />;
};

export default memo(PaymentV2);
